import React, { useState, useEffect } from 'react';
import { useOrion } from '../context/OrionContext';
import Icon from './Icon';
import { infoPrioridade } from '../services/prioridade';
import { situacaoDoSla, textoDoSla, rotuloDoStatus } from '../services/painelGerencia';
import { textoDoPulo } from '../services/puloFila';

// ─────────────────────────────────────────────────────────────────────────────
// Os dois diálogos da coleta de ticket. Quem abre é `assumirAoAtender`, no
// contexto, e espera a resposta — por isso vivem no shell, por cima de
// qualquer tela que tenha disparado a coleta (Filas, resumo do ticket).
//
//   · PULO — havia ticket livre à frente na fila. Mostra quantos e quais, e só
//     deixa puxar com justificativa: ela vai para a gerência apurar.
//   · MESMO CLIENTE — o ticket foi assumido e há outros livres do mesmo
//     cliente. Pergunta se o analista quer puxar todos (ou alguns) de uma vez.
// ─────────────────────────────────────────────────────────────────────────────

/** Justificativa curta demais não explica nada para quem apura. */
const MINIMO_MOTIVO = 15;

/** Uma linha de ticket, compacta, com prioridade, status e SLA. */
function LinhaTicket({ ticket, agora, posicao = null, children = null }) {
  const prio = infoPrioridade(ticket);
  const estourado = situacaoDoSla(ticket, agora) === 'estourado';
  return (
    <div className={`coleta-linha ${prio?.classe || ''}`}>
      {children}
      {posicao != null && <span className="coleta-pos">{posicao}º</span>}
      <div className="coleta-linha-txt">
        <div className="coleta-linha-meta">
          <b>#{ticket.codigo}</b>
          {prio && <span className={`prio-chip ${prio.classe}`}>{prio.rotulo}</span>}
          <span className="coleta-status">{rotuloDoStatus(ticket.codigoStatusTicket)}</span>
          <span className={`coleta-sla${estourado ? ' coleta-sla-estourado' : ''}`}>{textoDoSla(ticket, agora)}</span>
        </div>
        <div className="coleta-linha-titulo">{String(ticket.titulo || '').trim() || 'Sem título'}</div>
      </div>
    </div>
  );
}

function DialogoPulo({ dados, responder }) {
  const [motivo, setMotivo] = useState('');
  const agora = dados.agora || new Date();
  const curto = motivo.trim().length < MINIMO_MOTIVO;

  return (
    <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
      <div className="drawer-head">
        <div className="drawer-title">
          Pulo de fila
          <span className="drawer-sub">ticket {dados.numero} · {dados.posicao}º da fila</span>
        </div>
        <button className="drawer-close" onClick={() => responder(null)}><Icon name="x" size={15} /></button>
      </div>

      <div className="drawer-body">
        <div className="rt-aviso coleta-aviso">
          <Icon name="alert-triangle" size={14} />
          <div>
            <b>{textoDoPulo(dados.pulados, agora)}</b>{' '}
            Puxar o ticket {dados.numero} agora conta como <b>{dados.pulados.length === 1 ? '1 ticket pulado' : `${dados.pulados.length} tickets pulados`}</b>,
            e a justificativa vai para a gerência apurar.
          </div>
        </div>

        <div className="field-label coleta-rot">Tickets que ficam para trás</div>
        <div className="coleta-lista">
          {dados.pulados.map((t, i) => (
            <LinhaTicket key={t.codigo} ticket={t} agora={agora} posicao={i + 1} />
          ))}
        </div>

        <label className="field-label coleta-rot" htmlFor="coleta-motivo">Justificativa</label>
        <textarea
          id="coleta-motivo"
          rows={3}
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
          placeholder="Ex.: cliente ligou cobrando este ticket; os da frente dependem de retorno do cliente…"
          autoFocus
        />
        <div className="campo-dica">
          {curto
            ? `Escreva ao menos ${MINIMO_MOTIVO} caracteres — é o que a gerência vai ler.`
            : 'A justificativa é gravada com o seu nome e a lista acima.'}
        </div>
      </div>

      <div className="drawer-foot">
        <button className="btn" onClick={() => responder(null)}>Cancelar</button>
        <button
          className="btn btn-primary"
          disabled={curto}
          onClick={() => responder({ motivo: motivo.trim() })}
        >
          <Icon name="ticket" size={13} /> Justificar e puxar
        </button>
      </div>
    </div>
  );
}

function DialogoMesmoCliente({ dados, responder }) {
  const agora = dados.agora || new Date();
  // Todos marcados de partida: a pergunta é "quer puxar todos?", e desmarcar
  // é a exceção.
  const [marcados, setMarcados] = useState(() => new Set(dados.tickets.map(t => t.codigo)));
  const alternar = (codigo) => setMarcados((m) => {
    const novo = new Set(m);
    if (novo.has(codigo)) novo.delete(codigo); else novo.add(codigo);
    return novo;
  });
  const [motivo, setMotivo] = useState('');
  const selecionados = dados.tickets.filter(t => marcados.has(t.codigo));
  const ignorados = dados.tickets.filter(t => !marcados.has(t.codigo));
  const quem = dados.nomeCliente || `o cliente ${dados.cliente}`;
  // Deixar QUALQUER ticket do cliente na fila — a lista inteira ou parte dela —
  // pede justificativa. Ela vai para a gerência junto com os pulos.
  const precisaMotivo = ignorados.length > 0;
  const motivoCurto = motivo.trim().length < MINIMO_MOTIVO;
  const bloqueado = precisaMotivo && motivoCurto;

  const confirmar = () => responder({
    selecionados, ignorados, motivo: precisaMotivo ? motivo.trim() : '',
  });

  // Desmarcar todos é o mesmo que "ignorar a lista": um atalho para isso.
  const ignorarTodos = () => setMarcados(new Set());

  let rotuloConfirmar;
  if (!ignorados.length) rotuloConfirmar = ' Puxar todos';
  else if (!selecionados.length) rotuloConfirmar = ` Ficar só com o ${dados.numero}`;
  else rotuloConfirmar = ` Puxar ${selecionados.length} e deixar ${ignorados.length}`;

  return (
    <div className="modal-nx modal-largo" onClick={e => e.stopPropagation()}>
      {/* Sem botão de fechar: o ticket principal já foi assumido, e sair daqui
          é puxar todos ou justificar os que ficam. */}
      <div className="drawer-head">
        <div className="drawer-title">
          Outros tickets do mesmo cliente
          <span className="drawer-sub">{quem}</span>
        </div>
      </div>

      <div className="drawer-body">
        <div className="campo-dica coleta-intro">
          O ticket {dados.numero} já está com você. {dados.tickets.length === 1
            ? 'Há mais 1 ticket livre'
            : `Há mais ${dados.tickets.length} tickets livres`} de <b>{quem}</b> na fila.
          Quer puxar para o seu nome e resolver tudo no mesmo contato? Deixar algum
          na fila pede uma justificativa, que vai para a gerência.
        </div>

        <div className="coleta-lista">
          {dados.tickets.map(t => (
            <label key={t.codigo} className="coleta-marcavel">
              <LinhaTicket ticket={t} agora={agora}>
                <input
                  type="checkbox"
                  checked={marcados.has(t.codigo)}
                  onChange={() => alternar(t.codigo)}
                />
              </LinhaTicket>
            </label>
          ))}
        </div>

        {precisaMotivo && (
          <>
            <label className="field-label coleta-rot" htmlFor="coleta-motivo-cliente">
              Justificativa — {ignorados.length === 1
                ? '1 ticket do cliente fica na fila'
                : `${ignorados.length} tickets do cliente ficam na fila`}
            </label>
            <textarea
              id="coleta-motivo-cliente"
              rows={3}
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              placeholder="Ex.: o outro ticket é de outro setor do cliente; depende de retorno do Sistemas…"
              autoFocus
            />
            <div className="campo-dica">
              {motivoCurto
                ? `Escreva ao menos ${MINIMO_MOTIVO} caracteres — a justificativa vai para a gerência apurar.`
                : 'A justificativa é gravada com o seu nome e os tickets que ficaram.'}
            </div>
          </>
        )}
      </div>

      <div className="drawer-foot">
        {selecionados.length > 0 && (
          <button className="btn" onClick={ignorarTodos} title="Desmarca todos — pede justificativa">
            Ignorar a lista
          </button>
        )}
        <button
          className={`btn ${ignorados.length ? '' : 'btn-primary'}`.trim()}
          disabled={bloqueado}
          onClick={confirmar}
          title={bloqueado ? 'Justifique os tickets que ficam na fila' : undefined}
        >
          <Icon name={selecionados.length ? 'ticket' : 'check'} size={13} />
          {rotuloConfirmar}
        </button>
      </div>
    </div>
  );
}

export default function DialogoColeta() {
  const { dialogoColeta, responderColeta } = useOrion();

  // Esc cancela o pulo. No de mesmo cliente ele NÃO fecha — ignorar a lista
  // pede justificativa —, mas é engolido aqui para não fechar o que está atrás.
  useEffect(() => {
    if (!dialogoColeta) return undefined;
    const aoTeclar = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (dialogoColeta.tipo === 'pulo') responderColeta(null);
    };
    window.addEventListener('keydown', aoTeclar, true);
    return () => window.removeEventListener('keydown', aoTeclar, true);
  }, [dialogoColeta, responderColeta]);

  if (!dialogoColeta) return null;

  return (
    <div className="drawer-overlay coleta-overlay">
      {dialogoColeta.tipo === 'pulo'
        ? <DialogoPulo key={dialogoColeta.numero} dados={dialogoColeta} responder={responderColeta} />
        : <DialogoMesmoCliente key={dialogoColeta.numero} dados={dialogoColeta} responder={responderColeta} />}
    </div>
  );
}
